import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, Box, Typography, IconButton } from '@mui/material';
import { X } from 'lucide-react';
import AnalyticsLineChart from '../../../common_components/AnalyticsLineChart/AnalyticsLineChart';
import CommonLoader from '../../../common_components/CommonLoader';
import { fetchBenchmarkHistory, clearBenchmarkHistory, selectBenchmarkHistory, selectBenchmarkHistoryLoading, selectBenchmarkHistoryError, } from '../../../redux/adTestingSlice';
const WINDOW_TABS = [
    { key: '7d', label: '7 days' },
    { key: '30d', label: '30 days' },
    { key: '90d', label: '90 days' },
];
const RANGE_TABS = [
    { months: 3, label: '3 Months' },
    { months: 6, label: '6 Months' },
    { months: 12, label: 'Year' },
];
// Series colors — standardized to match the app-wide chart metric palette.
const COLORS = { roas: '#7A5BB6', cpoc: '#D19A3A', octr: '#5A95D6', cpa: '#F79009' };
// Compact segmented control (reused for both toggles).
const Segmented = ({ value, options, onChange }) => (_jsx(Box, { sx: { display: 'inline-flex', gap: '4px', p: '3px', bgcolor: '#F9FAFB', border: '1px solid #E9EAEB', borderRadius: '8px' }, children: options.map((o) => {
        const active = o.key === value;
        return (_jsx(Box, { onClick: () => onChange(o.key), className: "all-Font-Sizes-12", sx: {
                fontSize: '14px !important',
                px: 1.75,
                py: '6px',
                borderRadius: '6px',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: active ? 600 : 500,
                color: active ? '#101828' : '#667085',
                bgcolor: active ? '#FFFFFF' : 'transparent',
                border: active ? '1px solid #E9EAEB' : '1px solid transparent',
                boxShadow: active ? '0px 1px 2px 0px rgba(10,13,18,0.05)' : 'none',
            }, children: o.label }, String(o.key)));
    }) }));
const BenchmarkHistoryModal = ({ open, onClose, clientId, connectionId, funnel, title, showRangeTabs = true }) => {
    const dispatch = useDispatch();
    const data = useSelector(selectBenchmarkHistory);
    const loading = useSelector(selectBenchmarkHistoryLoading);
    const error = useSelector(selectBenchmarkHistoryError);
    const [windowKey, setWindowKey] = useState('7d');
    const [months, setMonths] = useState(3);
    // Fetch when opened or the funnel / account / range changes (window toggles client-side).
    useEffect(() => {
        if (!open || !clientId || !connectionId)
            return;
        dispatch(fetchBenchmarkHistory({ clientId, connectionId, funnel, months }));
    }, [dispatch, open, clientId, connectionId, funnel, months]);
    useEffect(() => {
        if (!open)
            dispatch(clearBenchmarkHistory());
    }, [dispatch, open]);
    const points = data?.points || [];
    const categories = useMemo(() => points.map((p) => p.date), [points]);
    const series = useMemo(() => {
        const roas = [];
        const cpoc = [];
        const octr = [];
        const cpa = [];
        points.forEach((p) => {
            const m = p.windows?.[windowKey] || { cpoc: 0, octr: 0, roas: 0, cpa: 0 };
            roas.push(Number(m.roas || 0));
            cpoc.push(Number(m.cpoc || 0));
            octr.push(Number(m.octr || 0));
            cpa.push(Number(m.cpa || 0));
        });
        return [
            { name: 'ROAS', data: roas, color: COLORS.roas },
            { name: 'CPOC', data: cpoc, color: COLORS.cpoc },
            { name: 'OCTR', data: octr, color: COLORS.octr },
            { name: 'CPA', data: cpa, color: COLORS.cpa },
        ];
    }, [points, windowKey]);
    const hasData = points.length > 0;
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "md", fullWidth: true, PaperProps: { sx: { borderRadius: '8px' } }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #EAECF0' }, children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: '16px', color: '#101828' }, children: title }), _jsx(IconButton, { onClick: onClose, size: "small", children: _jsx(X, { size: 20 }) })] }), _jsxs(DialogContent, { sx: { p: 3 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }, children: [_jsx(Segmented, { value: windowKey, options: WINDOW_TABS, onChange: setWindowKey }), showRangeTabs && (_jsx(Segmented, { value: months, options: RANGE_TABS.map((r) => ({ key: r.months, label: r.label })), onChange: setMonths }))] }), loading ? (_jsx(Box, { sx: { minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : error ? (_jsx(Box, { sx: { minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Typography, { sx: { color: '#B42318', fontSize: '14px' }, children: error }) })) : !hasData ? (_jsx(Box, { sx: { minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Typography, { sx: { color: '#667085', fontSize: '14px' }, children: "No benchmark history available for this account yet." }) })) : (_jsx(AnalyticsLineChart, { categories: categories, series: series, height: 380, showLegend: true }))] })] }));
};
export default BenchmarkHistoryModal;
